import * as React from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';

import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { EnumModel, InstructorFilterModel } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';

import ReactSelect, { ReactSelectValue, ReactSelectSize, ReactSelectOption } from '@app/Components/ReactSelect';
import { shallowEqual } from 'shallow-equal-object';
import { applyDecorators } from "@app/Helpers/Decorator";

type InstructorPickerProps = {
    clearable?: boolean;
    multi?: boolean;
    value?: string | null;
    selectedCrewFunction?: string | null;
    onSelect?: (value: string | null) => void;
    selectSize?: ReactSelectSize;
};

@observer
export default class InstructorPicker extends React.Component<InstructorPickerProps> {
    private _store: InstructorPickerStore;

    constructor(props: InstructorPickerProps) {
        super(props);
        applyDecorators(this);
        this._store = new InstructorPickerStore();
    }

    componentDidMount(): void {
        void this._store.loadData(this.props.selectedCrewFunction);
    }

    componentDidUpdate(prevProps: InstructorPickerProps): void {
        if (!shallowEqual(prevProps, this.props)) {
            this._store.loadData(this.props.selectedCrewFunction);
        }
    }

    render() {
        const { clearable, selectSize, multi, value } = this.props;
        return <ReactSelect placeholder="None" options={this._options} multi={multi} value={value} isClearable={clearable} selectSize={selectSize} onSelect={this._onChangeHandler} />;
    }

    @action.bound
    private _onChangeHandler(value: ReactSelectValue) {
        this.props.onSelect && this.props.onSelect(value ? value : null);
    }

    private get _options () {
        const options = this._store.options;
        if (this.props.value) {
            this.props.value.split(',').forEach(v => {
                if (!options.find(e => e.value === v)) {
                    options.push({ value: v, label: v.toUpperCase(), isDisabled: true });
                }
            });
        }
        return options;
    }
}

class InstructorPickerStore {
    @observable instructorFilters: InstructorFilterModel[] = [];
    private _currentFunctionGroup: string | null = null;

    constructor() {
        applyDecorators(this);
    }

    @action
    loadData(selectedCrewFunction?: string | null) {
        if (this._currentFunctionGroup !== selectedCrewFunction) {
            this._currentFunctionGroup = selectedCrewFunction || null;
            void this.loadInstructorFilter();
        }
    }

    @action.bound
    public async loadInstructorFilter() {
        const isForCabin = !this._currentFunctionGroup || this._currentFunctionGroup === FunctionGroupIds.Cabin || this._currentFunctionGroup === FunctionGroupIds.Fcm;
        const isForCockpit = !this._currentFunctionGroup || this._currentFunctionGroup === FunctionGroupIds.Cockpit || this._currentFunctionGroup === FunctionGroupIds.Fcm;
        const instructorFilters = await ApiService.getTypedData<InstructorFilterModel[]>(ApiUrls.InstructorFiltersUrl, { isForCabin, isForCockpit });
        this.instructorFilters = instructorFilters.data || [];
    }

    @computed
    public get options (): ReactSelectOption[] {
        return this.instructorFilters.map((type) => {
            return { value: type.code, label: type.name};
        });
    }
}
